import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    ChapterEnum,
    ProjectMethodologyEnum,
    SectionEnum,
    UerUpdateContractOtherCostFromCatalogMutationVariables,
    UerUpdateContractOtherCostMutationVariables,
    WorkOrderStatusEnum,
} from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import type { TypeItem } from '../../OtherCreate/commonData';
import { uerUpdateContractOtherCostGql, uerUpdateOtherCostFromCatalogGql } from '../editOtherQueries';
import { getUpdateInput } from '../utils';

export interface IUseUpdateContractOtherCost {
    contractId: number;
    workOrderId?: number;
    otherId: number;
    workOrderStatus?: WorkOrderStatusEnum;
    selectedType: TypeItem;
    selectedChapterOrSection: { id: ChapterEnum; label: string } | { id: SectionEnum; label: string };
    isCalculationTypeEditable: boolean;
    workOrderOtherPriceId?: number;
    isOtherFromCatalog?: boolean;
    isUpdateContractCondition?: boolean;
    methodology: ProjectMethodologyEnum;
    refreshProject?: () => void;
    isOnlyTypeEditAvailable?: boolean;
}

const requestIdFromCatalog = 'uerUpdateContractOtherCostFromCatalog';
const requestId = 'uerUpdateContractOtherCost';

export const useUerUpdateContractOtherCost = (props: IUseUpdateContractOtherCost) => {
    const {
        contractId,
        selectedChapterOrSection,
        selectedType,
        isCalculationTypeEditable,
        isOtherFromCatalog,
        otherId,
        refreshProject,
    } = props;

    const onSuccessHandler = useCallback(() => {
        refreshProject?.();
    }, [refreshProject]);

    const dispatch = useDispatch();

    const [
        updateContractOtherCostFromCatalogData,
        updateContractOtherCostFromCatalogStatus,
        ,
        updateContractOtherCostFromCatalogRequest,
    ] = useRequest({
        requestId: requestIdFromCatalog,
        query: uerUpdateOtherCostFromCatalogGql,
        withEffect: false,
    });

    const [uerUpdateContractOtherCostData, uerUpdateContractOtherCostStatus, , uerUpdateContractOtherCostRequest] =
        useRequest({
            requestId,
            query: uerUpdateContractOtherCostGql,
            withEffect: false,
            dependenciesUpdater: onSuccessHandler,
        });

    const [resetFromCatalog] = useResetStoreData({ requestId: requestIdFromCatalog });
    const [resetUpdate] = useResetStoreData({ requestId });

    const makeRequest = useCallback(
        (data: any, limitsArray: any[], formula?: string) => {
            const { updateInput } = getUpdateInput({
                data,
                contractId,
                selectedChapterOrSection,
                selectedType,
                isCalculationTypeEditable,
                otherId,
                limitsArray,
            });

            if (isOtherFromCatalog) {
                const variables: UerUpdateContractOtherCostFromCatalogMutationVariables = {
                    contractId,
                    updateInput: {
                        ...updateInput,
                        formula,
                    },
                };

                dispatch(updateContractOtherCostFromCatalogRequest(variables));

                return;
            }

            const variables: UerUpdateContractOtherCostMutationVariables = {
                contractId,
                updateInput: {
                    ...updateInput,
                    formula,
                },
            };

            dispatch(uerUpdateContractOtherCostRequest(variables));
        },
        [
            contractId,
            selectedChapterOrSection,
            selectedType,
            isCalculationTypeEditable,
            otherId,
            isOtherFromCatalog,
            dispatch,
            uerUpdateContractOtherCostRequest,
            updateContractOtherCostFromCatalogRequest,
        ],
    );

    const data = useMemo(() => {
        if (isOtherFromCatalog) {
            return updateContractOtherCostFromCatalogData?.uerUpdateContractOtherCostFromCatalog;
        }

        return uerUpdateContractOtherCostData?.uerUpdateContractOtherCost;
    }, [
        isOtherFromCatalog,
        uerUpdateContractOtherCostData?.uerUpdateContractOtherCost,
        updateContractOtherCostFromCatalogData?.uerUpdateContractOtherCostFromCatalog,
    ]);

    const status = useMemo(() => {
        if (isOtherFromCatalog) {
            return updateContractOtherCostFromCatalogStatus;
        }

        return uerUpdateContractOtherCostStatus;
    }, [isOtherFromCatalog, uerUpdateContractOtherCostStatus, updateContractOtherCostFromCatalogStatus]);

    const reset = useMemo(
        () => (isOtherFromCatalog ? resetFromCatalog : resetUpdate),
        [isOtherFromCatalog, resetUpdate, resetFromCatalog],
    );

    return {
        makeRequest,
        reset,
        data,
        status,
    };
};
